import React, { useState, useEffect,useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getUsers, sendPushNotification } from '../services/userService';
import NotificationModal from '../components/NotificationModal';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await getUsers();
        setUsers(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch users: ' + err.message);
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.mobile && user.mobile.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
    }
  };

  const openNotificationModal = () => {
    if (selectedUsers.length === 0) {
      setError('Please select at least one user to send a notification');
      return;
    }
    setIsModalOpen(true);
  };

  const closeNotificationModal = () => {
    setIsModalOpen(false);
  };

  const handleSendNotification = async (message) => {
    setSendingNotification(true);
    
    try {
      const response = await sendPushNotification(selectedUsers, message);
      
      setSendingNotification(false);
      
      // If notification was successful, clear selected users after modal closes
      if (response.success && response.details?.failed === 0) {
        setTimeout(() => {
          setSelectedUsers([]);
        }, 1600); // Slightly longer than the modal close timeout
      }
      
      return response;
    } catch (err) {
      setSendingNotification(false);
      throw err;
    }
  };


  return (
    <div className="user-management-container">
      <h1>User Management</h1>
      
      <div className="search-container">
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="actions-toolbar">
        {selectedUsers.length > 0 && (
          <button 
            onClick={openNotificationModal}
            className="send-notification-button"
          >
            Send Notification to Selected ({selectedUsers.length})
          </button>
        )}
      </div>
      
      {loading ? (
        <div className="loading-indicator">Loading users...</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="users-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Mobile</th>
                  <th>Target Sugar</th>
                  <th>Insulin Sensitivity</th>
                  <th>Carb Ratio</th>
                  <th>Created</th>
                  <th>Last Login</th> 
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="user-row">
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleSelectUser(user.id)}
                        />
                      </td>
                      <td className="user-name">{user.name}</td>
                      <td className="user-email">{user.email}</td>
                      <td className="user-mobile">{user.mobile || '-'}</td>
                      <td className="user-target">{user.targetSugarLevel} mmol/L</td>
                      <td className="user-sensitivity">{user.insulinSensitivityFactor}</td>
                      <td className="user-carb-ratio">{user.carbRatio} g/unit</td>
                      <td className="user-created">{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td className="user-lastLogin">
                        {user.lastLogin 
                          ? new Date(user.lastLogin).toLocaleString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            })
                          : '-'}
                      </td>
                      <td className="user-actions">
                        <button 
                          onClick={() => navigate(`/sugar-measurements/user/${user.id}`)}
                          className="view-measurements-button"
                        >
                          View Measurements
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="no-results-row">
                    <td colSpan="9">No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          <div className="user-count">
            <span>Showing {filteredUsers.length} of {users.length} users</span>
          </div>
        </>
      )}
      
      <div className="actions-container">
        <button 
          onClick={() => navigate('/dashboard')}
          className="back-button"
        >
          Back to Dashboard
        </button>
      </div>
      
      {/* Render the notification modal */}
      <NotificationModal
        isOpen={isModalOpen}
        onClose={closeNotificationModal}
        onSend={handleSendNotification}
        selectedUsers={selectedUsers}
        sending={sendingNotification}
      />
  </div>
  );
};

export default UserList;